/**
 * `@` mentions in the composer - ships and signals, the way Teams tags people.
 *
 * Two things this does, and the second is the point:
 *
 * 1. **Typing aid.** You type `@aida` and pick AIDAbella, and the exact name
 *    goes into the message instead of an approximation of it.
 * 2. **Confirmed context.** What you picked travels with the message as ids, so
 *    the assistant does not have to go looking for the signal you already
 *    named. That removes a `search_columns` round trip and, more usefully,
 *    removes the chance of it proposing a column that does not exist.
 *
 * Everything here is a pure function over `(text, caret)` so the fiddly parts -
 * where a trigger starts, what survives an edit - are testable without a DOM.
 * The composer stays a plain `<textarea>`: a contenteditable would buy pills at
 * the cost of IME input, undo, paste and screen readers, which is a bad trade
 * for rounded corners.
 */

import { fuzzyScore } from '../../utils/fuzzy.ts';

export type MentionKind = 'ship' | 'signal';

export interface MentionItem {
  kind: MentionKind;
  /** What travels to the backend: a ship id, or an exact column name. */
  id: string;
  /** What the person reads and what is typed into the message. */
  label: string;
  /** The quiet second line - a brand, a unit, the id behind a friendly name. */
  hint?: string;
}

/** An `@…` token being typed, with where it starts and what is in it so far. */
export interface MentionTrigger {
  /** Index of the `@`. */
  start: number;
  /** Text between the `@` and the caret. */
  query: string;
}

/** A mention that made it into the final message. */
export interface ResolvedMention {
  kind: MentionKind;
  id: string;
  label: string;
}

/**
 * The `@…` being typed at the caret, if there is one.
 *
 * The `@` only counts at a word boundary, so an email address does not open a
 * menu. The query stops at a newline; a space is allowed because ship names
 * have them ("AIDAbella" does not, but "Mein Schiff 4" would), and the menu
 * closes on its own once nothing matches.
 */
export function findTrigger(text: string, caret: number): MentionTrigger | null {
  const upto = text.slice(0, caret);
  const at = upto.lastIndexOf('@');
  if (at < 0) return null;

  const before = at === 0 ? '' : (upto[at - 1] as string);
  if (before && !/[\s(,;]/.test(before)) return null;

  const query = upto.slice(at + 1);
  if (query.includes('\n')) return null;
  // Two spaces in a row means the sentence moved on without picking anything.
  if (/\s\s/.test(query)) return null;
  if (query.length > 40) return null;
  return { start: at, query };
}

/**
 * Best matches for what has been typed.
 *
 * Ships come first on an equal score: there are 56 of them against several
 * hundred signals, and naming the vessel is the more common thing to do.
 */
export function rankMentions(
  query: string,
  items: readonly MentionItem[],
  limit = 8,
): MentionItem[] {
  const q = query.trim();
  if (!q) {
    // Nothing typed yet: offer ships first, since that is where most questions
    // start, then whatever signals are to hand.
    const ships = items.filter((i) => i.kind === 'ship').slice(0, limit);
    const rest = items.filter((i) => i.kind === 'signal').slice(0, Math.max(0, limit - ships.length));
    return [...ships, ...rest];
  }
  const scored: Array<{ item: MentionItem; score: number }> = [];
  for (const item of items) {
    // `fuzzyScore` returns null for "not a match at all", which is not the same
    // as scoring zero - a name is searchable by what it is called and by its
    // id, and the better of the two decides where it ranks.
    const onLabel = fuzzyScore(q, item.label);
    const onId = item.id === item.label ? null : fuzzyScore(q, item.id);
    if (onLabel === null && onId === null) continue;
    scored.push({ item, score: Math.max(onLabel ?? 0, onId ?? 0) });
  }
  scored.sort((a, b) => {
    if (b.score !== a.score) return b.score - a.score;
    if (a.item.kind !== b.item.kind) return a.item.kind === 'ship' ? -1 : 1;
    return a.item.label.localeCompare(b.item.label);
  });
  return scored.slice(0, limit).map((s) => s.item);
}

/** Text as it is written into the message for a pick. */
export function mentionText(item: MentionItem): string {
  return item.label;
}

/**
 * Replace the `@…` being typed with the chosen name.
 *
 * A trailing space follows the insertion, because the next thing a person types
 * is another word, and having to press space after every pick is the kind of
 * small friction that makes a feature feel cheap.
 */
export function applyMention(
  text: string,
  trigger: MentionTrigger,
  item: MentionItem,
  caret: number,
): { text: string; caret: number } {
  const inserted = `${mentionText(item)} `;
  const next = text.slice(0, trigger.start) + inserted + text.slice(caret);
  return { text: next, caret: trigger.start + inserted.length };
}

/**
 * Which picks are still in the message.
 *
 * Picking something and then deleting it must not leave it in the payload -
 * "confirmed context" that the person removed is worse than no context at all,
 * because the model would trust it. So the final text is the authority: a
 * mention counts only if its name is still written there.
 */
export function resolveMentions(
  text: string,
  picked: readonly MentionItem[],
): ResolvedMention[] {
  const seen = new Set<string>();
  const out: ResolvedMention[] = [];
  for (const item of picked) {
    const key = `${item.kind}:${item.id}`;
    if (seen.has(key)) continue;
    if (!text.includes(mentionText(item))) continue;
    seen.add(key);
    out.push({ kind: item.kind, id: item.id, label: item.label });
  }
  return out;
}

/** One run of text, marked if it is a name that was tagged. */
export interface MentionSegment {
  text: string;
  kind?: MentionKind;
}

/**
 * Split a message into plain runs and tagged names.
 *
 * Used twice: to paint the composer through a highlight layer behind the
 * textarea, and to colour the same names in the message once it is sent. One
 * function, so what you see while typing is what you see afterwards.
 *
 * Longest label first, so tagging both `AIDA` and `AIDAbella` marks the longer
 * one rather than leaving `bella` hanging off the end of a match.
 */
export function splitByMentions(
  text: string,
  mentions: ReadonlyArray<{ kind: MentionKind; label: string }>,
): MentionSegment[] {
  const names = [...mentions]
    .filter((m) => m.label)
    .sort((a, b) => b.label.length - a.label.length);
  if (!names.length || !text) return [{ text }];

  const out: MentionSegment[] = [];
  let i = 0;
  let plain = '';
  outer: while (i < text.length) {
    for (const m of names) {
      if (text.startsWith(m.label, i)) {
        if (plain) {
          out.push({ text: plain });
          plain = '';
        }
        out.push({ text: m.label, kind: m.kind });
        i += m.label.length;
        continue outer;
      }
    }
    plain += text[i];
    i += 1;
  }
  if (plain) out.push({ text: plain });
  return out;
}
